import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { AppCTX } from '../data/AppContext';

const SearchPlaces = () => {
  const map = useMap();
  const places = useMapsLibrary('places');
  const [input, setInput] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const autocompleteService = React.useRef(null);
  const geocoder = React.useRef(null);
  
  // Get context values for updating newProspect
  const { setNewProspect, setStatus } = React.useContext(AppCTX);

  React.useEffect(() => {
    if (places && !autocompleteService.current) {
      autocompleteService.current = new places.AutocompleteService();
    }
    if (window.google && window.google.maps && !geocoder.current) {
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, [places]);

  React.useEffect(() => {
    let active = true;
    if (!autocompleteService.current || !input) {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    autocompleteService.current.getPlacePredictions({ input }, (results) => {
      if (active) {
        setOptions(results || []);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [input]);

  const handleSelect = (event, value) => {
    if (!value || !geocoder.current || !map) return;
    
    geocoder.current.geocode({ placeId: value.place_id }, (results, status) => {
      if (status === 'OK' && results && results[0].geometry && results[0].geometry.location) {
        const loc = results[0].geometry.location;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        
        // Pan to the location
        map.setCenter(coords);
        map.setZoom(18);
        
        // Update newProspect with coordinates (same as clicking on map)
        setNewProspect(prev => ({
          ...prev,
          coords: coords,
          // Clear address and other data since we're setting new coordinates
          address: '',
          owners: [],
          markerStatus: 'default',
          propertyDetails: null
        }));
        
        // Clear the search input
        setInput('');
        
        // Show success notification
        setStatus({ 
          notificationType: 'success', 
          message: 'Location selected! You can now get insights or add a prospect.' 
        });
        
        console.log('📍 SearchPlaces: Location selected and coordinates added to newProspect:', coords);
      } else {
        setStatus({ 
          notificationType: 'error', 
          message: 'Could not get location details. Please try again.' 
        });
      }
    });
  };

  // Don't render if the API isn't loaded yet
  if (!places || !window.google || !window.google.maps) {
    return (
      <Box
        sx={{
          width: '100%',
          background: '#181818',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          p: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextField 
          label="Loading..." 
          fullWidth 
          disabled
          InputLabelProps={{ style: { color: '#bbb' } }}
          InputProps={{
            style: { color: '#fff', background: '#222', borderRadius: 8 },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        background: '#181818',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        p: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Autocomplete
        freeSolo
        filterOptions={(x) => x}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        loading={loading}
        onInputChange={(_, value) => setInput(value)}
        onChange={handleSelect}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            background: '#222',
            color: '#fff',
            borderRadius: 2,
            fontSize: '1rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#444',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#888',
          },
          '& .MuiInputLabel-root': {
            color: '#bbb',
          },
          '& .MuiAutocomplete-popupIndicator': {
            color: '#fff',
          },
          '& .MuiAutocomplete-clearIndicator': {
            color: '#fff',
          },
        }}
        renderInput={(params) => (
          <TextField {...params} label="Search Location" fullWidth 
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{
              ...params.InputProps,
              style: { color: '#fff', background: '#222', borderRadius: 8 },
            }}
          />
        )}
        renderOption={(props, option) => {
          const matches = option.structured_formatting?.main_text_matched_substrings || [];
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((m) => [m.offset, m.offset + m.length])
          );
          return (
            <li {...props} style={{ background: '#181818', color: '#fff', borderBottom: '1px solid #222' }}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  {parts.map((part, idx) => (
                    <Box key={idx} component="span" sx={{ fontWeight: part.highlight ? 'bold' : 'regular', color: part.highlight ? '#fff' : '#bbb' }}>{part.text}</Box>
                  ))}
                  <Typography variant="body2" color="#bbb">
                    {option.structured_formatting?.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </Box>
  );
};

export default SearchPlaces;